import React, { useState } from "react";
import { Spin } from "antd";
import {
  DownOutlined,
  EditOutlined,
  LogoutOutlined,
  CheckOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import CustomerInfoDrawer from "../user/CustomerInfoDrawer";
import { fmtBj } from "../../utils/bjDate";
import { CONTRACT_STATUS_MAP, RENT_CYCLE_MAP } from "./rentBillConstants";

const CYCLE_UNIT = { daily: "天", monthly: "月", quarterly: "季", yearly: "年" };

// 每月 / 每季 / 每3月 / 每2季 ...
const cycleText = (cycle, multiplier) => {
  const unit = CYCLE_UNIT[cycle];
  if (!unit) return "";
  const n = Number(multiplier) || 1;
  return n > 1 ? `每${n}${unit}` : `每${unit}`;
};

const num = (v) =>
  v == null || v === ""
    ? "-"
    : Number(v).toLocaleString("zh-CN", { maximumFractionDigits: 2 });

const Chip = ({ ok, yes, no }) => (
  <span className={`rcd-chip ${ok ? "ok" : "no"}`}>
    {ok ? <CheckOutlined /> : <CloseOutlined />}
    {ok ? yes : no}
  </span>
);

/**
 * 合约信息（可折叠）
 * 第1行 房间号/周期/状态/箭头（点击整行折叠，折叠只收起下面的信息行）
 * 第2行 操作条（常驻）  第3~7行 信息
 * 折叠状态保存在本组件内，切换上一条/下一条时保持用户上次的选择
 */
export default function ContractInfoPanel({
  contract,
  loading,
  onEdit,
  onCheckOut,
  checkingOut = false,
}) {
  const [open, setOpen] = useState(true);
  const [customerDrawerOpen, setCustomerDrawerOpen] = useState(false);

  if (!contract) {
    return (
      <div className="rcd-glass" style={{ padding: 28, textAlign: "center" }}>
        {loading ? <Spin /> : <span className="rcd-mute">暂无合约信息</span>}
      </div>
    );
  }

  const statusCfg = CONTRACT_STATUS_MAP[contract.status] || {
    label: contract.status,
    color: "default",
  };
  const multiplier = Number(contract.rentPaymentCycleMultiplier) || 1;
  const cycleTag =
    (RENT_CYCLE_MAP[contract.rentPaymentCycle] || "") +
    (multiplier > 1 ? `×${multiplier}` : "");

  const dep = Number(contract.securityDeposit) || 0;
  const depPaid = Number(contract.securityDepositPayed) || 0;
  const depColor =
    dep <= 0
      ? "#94a3b8"
      : depPaid >= dep
        ? "#16a34a"
        : depPaid > 0
          ? "#d97706"
          : "#dc2626";
  const prePaid = Number(contract.prePaidDays) || 0;

  const signText = contract.contractSignDate
    ? `${fmtBj(contract.contractSignDate)}签约`
    : "";
  const rangeText = `${fmtBj(contract.contractStartDate)}～${fmtBj(contract.contractExpiryDate)}`;

  const client = contract.client;

  return (
    <div className="rcd-glass" style={{ opacity: loading ? 0.7 : 1 }}>
      {/* 第1行：房间号 / 周期 / 状态 / 折叠箭头 */}
      <button
        type="button"
        className="rcd-info-head"
        aria-expanded={open}
        aria-controls="rcd-info-body"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="rcd-room">
          {contract.host?.roomNumber || "未关联房间"}
        </span>
        {cycleTag && (
          <span className="rcd-chip" style={{ background: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe" }}>
            {cycleTag}
          </span>
        )}
        <span
          className="rcd-chip"
          style={{
            background:
              statusCfg.color === "success"
                ? "#f0fdf4"
                : statusCfg.color === "warning"
                  ? "#fffbeb"
                  : "#f1f5f9",
            color:
              statusCfg.color === "success"
                ? "#15803d"
                : statusCfg.color === "warning"
                  ? "#b45309"
                  : "#64748b",
            border: "1px solid rgba(148,163,184,.3)",
          }}
        >
          {statusCfg.label}
        </span>
        <DownOutlined className={`rcd-chev${open ? " open" : ""}`} />
      </button>

      {/* 第2行：操作条（后续扩展操作也放这里） */}
      <div className="rcd-ops">
        {contract.status === "active" && (
          <button
            type="button"
            className="rcd-btn sm danger"
            onClick={onCheckOut}
            disabled={checkingOut}
          >
            <LogoutOutlined />
            {checkingOut ? "处理中…" : "退房"}
          </button>
        )}
        <button
          type="button"
          className="rcd-btn sm"
          onClick={() => onEdit?.(contract)}
        >
          <EditOutlined />
          编辑
        </button>
      </div>

      <div
        id="rcd-info-body"
        className={`rcd-collapse${open ? "" : " closed"}`}
        aria-hidden={!open}
      >
        <div>
          {/* 第3行：客户(可点击) / 签约人 / 跟进人 */}
          <div className="rcd-line rcd-people">
            <span>
              <button
                type="button"
                className="rcd-link"
                disabled={!client?._id}
                onClick={() => setCustomerDrawerOpen(true)}
                aria-label={`查看客户资料：${client?.name || "未知客户"}`}
              >
                {client?.name || "-"}
              </button>
              <span className="rcd-mute">(客户)</span>
            </span>
            {contract.signer?.name && (
              <span>
                {contract.signer.name}
                <span className="rcd-mute">(签约)</span>
              </span>
            )}
            {contract.followUpPerson?.name && (
              <span>
                {contract.followUpPerson.name}
                <span className="rcd-mute">(跟进)</span>
              </span>
            )}
          </div>

          {/* 第4行：签约日期、起止日期 */}
          <div className="rcd-line">
            {signText && `${signText}，`}
            {rangeText}
          </div>

          {/* 第5行：付款周期、租金、提前天数、已付押金/押金 */}
          <div className="rcd-line">
            {cycleText(contract.rentPaymentCycle, multiplier)}支付
            {num(contract.rent)}元
            {prePaid > 0 && `，提前${prePaid}天`}，
            <span className="rcd-mute">押</span>
            <span style={{ color: depColor, fontWeight: 500 }}>
              {dep <= 0 ? "无押金" : `${num(depPaid)}/${num(dep)}`}
            </span>
          </div>

          {/* 第6行：是否包物业 / 水电 */}
          <div className="rcd-line rcd-chips">
            <Chip ok={!!contract.freePropertyFee} yes="包物业" no="不包物业" />
            <Chip
              ok={!!contract.freeWaterElectricity}
              yes="包水电"
              no="不包水电"
            />
          </div>

          {/* 第7行：备注（为空不显示） */}
          {contract.notes ? (
            <div className="rcd-line rcd-notes">{contract.notes}</div>
          ) : null}
        </div>
      </div>

      <CustomerInfoDrawer
        open={customerDrawerOpen}
        customerId={client?._id}
        onClose={() => setCustomerDrawerOpen(false)}
      />
    </div>
  );
}
